import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { TableCellNode, TableRowNode } from '@lexical/table';
import { $getNodeByKey, $createParagraphNode } from 'lexical';
import { useCallback, useMemo } from 'react';

export const useEditorTable = () => {
  const [editor] = useLexicalComposerContext();

  const addRowBelow = useCallback(
    (rowKey: string) => {
      editor.update(() => {
        const rowNode = $getNodeByKey(rowKey);
        if (!(rowNode instanceof TableRowNode)) {
          return;
        }

        const tableNode = rowNode.getParent();
        if (!tableNode) {
          return;
        }

        const columnCount = rowNode.getChildrenSize();

        const newRow = new TableRowNode();

        for (let i = 0; i < columnCount; i++) {
          const newCell = new TableCellNode();
          newRow.append(newCell);
        }

        rowNode.insertAfter(newRow);
      });
    },
    [editor],
  );

  const deleteRow = useCallback(
    (rowKey: string) => {
      editor.update(() => {
        const rowNode = $getNodeByKey(rowKey);
        if (!(rowNode instanceof TableRowNode)) {
          return;
        }

        const tableNode = rowNode.getParent();
        if (!tableNode) {
          return;
        }

        const prevRow = rowNode.getPreviousSibling();
        const nextRow = rowNode.getNextSibling();

        // if it's the only row in the table, we need to remove the table
        if (tableNode.getChildrenSize() === 1) {
          const fallback = $createParagraphNode();
          tableNode.insertAfter(fallback);
          fallback.select(0, 0);

          tableNode.remove();
          return;
        }

        if (prevRow instanceof TableRowNode) {
          prevRow.select();
        } else if (nextRow instanceof TableRowNode) {
          nextRow.select();
        }

        rowNode.remove();

        if (tableNode.getChildrenSize() === 0) {
          const fallback = $createParagraphNode();
          tableNode.insertAfter(fallback);
          fallback.select(0, 0);
          tableNode.remove();
        }
      });
    },
    [editor],
  );

  return useMemo(
    () => ({
      addRowBelow,
      deleteRow,
    }),
    [addRowBelow, deleteRow],
  );
};
